import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type { Word, UserProgress, PronunciationScore } from '../types';

interface Feedback {
    id: string;
    user_id: string;
    word_id: string;
    overall_score: number;
    accuracy: number;
    fluency: number;
    feedback_text: string;
    feedback_en: string;
    feedback_vi: string;
    suggestions: string[];
    suggestions_en: string[];
    suggestions_vi: string[];
    phoneme_breakdown: any[];
    created_at: string;
}

async function fetchWordData(wordId: string) {
    const { data: wordData, error: wordError } = await supabase
        .from('words')
        .select('*')
        .eq('id', wordId)
        .single();

    if (wordError) throw new Error(wordError.message);
    if (!wordData) throw new Error('Word not found.');

    const { data: progressData, error: progressError } = await supabase
        .from('user_word_progress')
        .select('*')
        .eq('word_id', wordId)
        .maybeSingle();

    const { data: feedbackData, error: feedbackError } = await supabase
        .from('pronunciation_feedback')
        .select('*')
        .eq('word_id', wordId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (progressError) console.warn('Could not fetch user progress', progressError);
    if (feedbackError) console.warn('Could not fetch last feedback', feedbackError);

    return {
        word: wordData as Word,
        userProgress: progressData as UserProgress | null,
        lastFeedback: feedbackData as Feedback | null,
    };
}

export function usePronunciationPractice(wordId: string) {
    const queryClient = useQueryClient();
    const [sessionId] = useState(() => crypto.randomUUID());
    const [currentScore, setCurrentScore] = useState<PronunciationScore | null>(null);
    const [isCelebrating, setIsCelebrating] = useState(false);

    const { data, error, isLoading } = useQuery({
        queryKey: ['word', wordId],
        queryFn: () => fetchWordData(wordId),
        enabled: !!wordId,
    });

    const handleScoreReceived = (score: PronunciationScore) => {
        setCurrentScore(score);

        if (score.overall >= 80) {
            setIsCelebrating(true);
            setTimeout(() => setIsCelebrating(false), 3000);
        }

        queryClient.invalidateQueries({ queryKey: ['word', wordId] });
    };

    return {
        wordData: data,
        error,
        isLoading,
        sessionId,
        currentScore,
        isCelebrating,
        handleScoreReceived,
    };
}